import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from './Modal.js';
import './CharacterQuickAddModal.css';

const NEW_PLAYER_VALUE = '__new__';

interface QuickAddPlayerOption {
  id: number;
  name: string;
}

interface CharacterQuickAddModalProps {
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    realm: string;
    playerId?: number;
    newPlayerName?: string;
  }) => void;
  isSaving: boolean;
  /**
   * When given, the modal also asks which player the character belongs to
   * (an existing one, or a brand-new one typed in on the spot) — the speed
   * dial's "add character" flow, which isn't scoped to one player the way
   * a ghost card's quick-add already is.
   */
  players?: QuickAddPlayerOption[];
}

/**
 * The normal way to add a character: name + realm only. Everything else —
 * class, spec, race, item level, gear — comes from the sync that runs right
 * after creation, so this form deliberately doesn't ask for it.
 */
export function CharacterQuickAddModal({
  onClose,
  onSubmit,
  isSaving,
  players,
}: CharacterQuickAddModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [realm, setRealm] = useState('');
  const [playerChoice, setPlayerChoice] = useState<string>(
    players && players.length > 0 ? String(players[0]!.id) : NEW_PLAYER_VALUE,
  );
  const [newPlayerName, setNewPlayerName] = useState('');

  const needsPlayerPick = players !== undefined;
  const isNewPlayer = needsPlayerPick && playerChoice === NEW_PLAYER_VALUE;
  const canSubmit =
    name.trim() !== '' && realm.trim() !== '' && (!isNewPlayer || newPlayerName.trim() !== '');

  function submit() {
    const base = { name: name.trim(), realm: realm.trim() };
    if (!needsPlayerPick) {
      onSubmit(base);
    } else if (isNewPlayer) {
      onSubmit({ ...base, newPlayerName: newPlayerName.trim() });
    } else {
      onSubmit({ ...base, playerId: Number(playerChoice) });
    }
  }

  return (
    <Modal
      title={t('roster.addCharacter')}
      onClose={onClose}
      width="sm"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSubmit || isSaving}
            onClick={submit}
          >
            {isSaving ? t('roster.syncing') : t('roster.addAndSync')}
          </button>
        </>
      }
    >
      <p className="character-quick-add-hint">{t('roster.quickAddHint')}</p>

      {needsPlayerPick && (
        <div className="field">
          <label htmlFor="quick-add-player">{t('roster.selectPlayer')}</label>
          <select
            id="quick-add-player"
            value={playerChoice}
            onChange={(event) => setPlayerChoice(event.target.value)}
            disabled={isSaving}
          >
            {players.map((player) => (
              <option key={player.id} value={player.id}>
                {player.name}
              </option>
            ))}
            <option value={NEW_PLAYER_VALUE}>{t('roster.newPlayerOption')}</option>
          </select>
        </div>
      )}

      {isNewPlayer && (
        <div className="field">
          <label htmlFor="quick-add-new-player">{t('roster.newPlayerName')}</label>
          <input
            id="quick-add-new-player"
            value={newPlayerName}
            onChange={(event) => setNewPlayerName(event.target.value)}
            disabled={isSaving}
          />
        </div>
      )}

      <div className="field">
        <label htmlFor="quick-add-name">{t('roster.name')}</label>
        <input
          id="quick-add-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoFocus
          disabled={isSaving}
        />
      </div>
      <div className="field">
        <label htmlFor="quick-add-realm">{t('roster.realm')}</label>
        <input
          id="quick-add-realm"
          value={realm}
          onChange={(event) => setRealm(event.target.value)}
          disabled={isSaving}
        />
      </div>
      {isSaving && (
        <div className="character-quick-add-syncing">
          <div className="spinner" />
          <span>{t('roster.syncingHint')}</span>
        </div>
      )}
    </Modal>
  );
}
